/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import type { CountryCode } from '@src/i18n/locales'
import { safeParse } from '@src/utils/string.util'

import {
  AuthProviders,
  CACHE,
  MIDDLEWARE_TIMEOUTS,
  ONE_MONTH_IN_SECONDS,
} from './utils/constants.util'

const vercelUrl = `https://${process.env.VERCEL_URL || 'www.beachbody.com'}`
const siteMapUrl = process.env.SITEMAP_URL_OVERRIDE || vercelUrl

/**
 * PUBLIC config values that may be exposed to the client.
 */
export interface ConfigPublic {
  general: {
    env: string
    isProduction: boolean
    vercelEnv: string
    isDeployed: () => boolean
    isCacheEnabled: () => boolean
    appVersion: string
    commitSHA: string
    test?: string
    beachbodyUrl: string
    bodUrl: string
    bodIosAppUrl: string
    bodAndroidAppUrl: string
    tbbUrl: string
    eligibilityRedirectUrl: string
    siteMapUrl: string
    supportedLocales: string[]
    isCartIconOnCheckout: boolean
    topNavDelay: number
    middlewareTimeouts: typeof MIDDLEWARE_TIMEOUTS
    inactivityTimeout: number
    webSourceCodesToDisableSetPassword: string[]
    authProvider: AuthProviders
    productMaxQuantities: {
      digital: number
      nutrition: number
      fitness: number
    }
  }
  shopify: {
    env: string
    baseUrlMap: {
      [key in CountryCode]: string
    }
  }
  logs: {
    logsVisibleInConsole: boolean
    logLevelClient: string
    logLevelServer: string
    sensitiveFieldPatterns: string[]
    safeFieldPatterns: string[]
  }
  amplitude: {
    clientDeploymentKey: string
    apiKey: string
    logLevel: number
  }
  builder: {
    apiKey: string
    cacheMap: Record<string, string>
  }
  cybersource: {
    iframeUrl: string
  }
  datadog: {
    allowedTracingUrls: string[]
    applicationId: string
    clientToken: string
    env: string
    isEnabled: boolean
    sessionSampleRate: number
    sessionReplaySampleRate: number
    site: string
    service: string
  }
  featureFlags: {
    partnerEnabled: boolean
    guestCheckoutEnabled: boolean
    myAccountPageEnabled: boolean
    savedAddressesEnabled: boolean
    savedPaymentsEnabled: boolean
    inspectUserPropertiesEnabled: boolean
    microformV2Enabled: boolean
    checkoutLoginEnabled: boolean
    BODEnabled: boolean
    faqEnabled: boolean
    shopifyEnabled: boolean
  }
  googleTagManager: {
    id: string
    auth?: string
    previewEnv?: string
  }
  googleRecaptcha: {
    siteKey: string
  }
  orderService: {
    apiUrl: string
    apiKey: string
    apiGatewayUrl: string
  }
  eventService: {
    apiUrl: string
    apiKey: string
    awsRegion: string
    awsService: string
  }
  osano: {
    configId: string
    configVersion: string
    region1CountryCodes: string[]
  }
  consentsService: {
    apiUrl: string
    apiKey: string
  }
  threatmetrix: {
    url: string
    orgId: string
  }
  affiliate: {
    cookieMaxAge: string
    webSourceCode: string
  }
  loudCrowd: {
    scriptUrl: string
    shopId: string
  }
  sse: {
    scriptUrl: string
    cssUrl: string
    isSplashed: boolean
    sseMigrationDate: string
    isLegacySseEnabled: boolean
  }
  applePay: {
    merchantId: string
    allowCardsFromAnyCountry: boolean
    isEnabled: boolean
    enableCriteria: Array<Record<string, string>>
    env: string
  }
  sierra: {
    isEnabled: boolean
    scriptUrl: string
    pathsToCloseChat: string[]
  }
  socialLadder: {
    isEnabled: boolean
    isConversionEnabled: boolean
    isSplashed: boolean
    cookieMaxAge: string
    areaGuid: {
      BodiAffiliates: string
      BodiExperience: string
    }
    appGuid: {
      BodiAffiliates: string
      BodiExperience: string
    }
    shopCode: {
      BodiAffiliates: string
      BodiExperience: string
    }
  }
  videoPlayer: {
    scriptUrl: string
    configUrl: string
  }
  gladly: {
    url: string
    orgId: string
    appId: Record<string, string>
    lng: Record<string, string>
    audienceId: Record<string, string>
  }
  formsite: {
    formUrls: Record<string, string>
  }
}

export const configPublic: ConfigPublic = {
  general: {
    env: process.env.NEXT_PUBLIC_ENV ?? 'local',
    isProduction: process.env.NEXT_PUBLIC_VERCEL_ENV === 'production',
    vercelEnv: String(process.env.NEXT_PUBLIC_VERCEL_ENV),
    isDeployed: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    isCacheEnabled: () => process.env.NEXT_PUBLIC_VERCEL_ENV !== undefined,
    appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? 'development',
    commitSHA: (process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? '').slice(0, 7),
    test: process.env.NEXT_PUBLIC_TEST,
    beachbodyUrl: process.env.NEXT_PUBLIC_BEACHBODY_URL ?? 'https://www.beachbody.com',
    bodUrl: process.env.NEXT_PUBLIC_BOD_URL ?? 'https://www.beachbodyondemand.com',
    bodIosAppUrl:
      process.env.NEXT_PUBLIC_BOD_IOS_APP_URL ??
      'https://apps.apple.com/us/app/beachbody-on-demand/id1031660123',
    bodAndroidAppUrl:
      process.env.NEXT_PUBLIC_BOD_ANDROID_APP_URL ??
      'https://play.google.com/store/apps/details?id=com.beachbody.bod',
    tbbUrl: process.env.NEXT_PUBLIC_TBB_URL ?? 'https://www.teambeachbody.com',
    eligibilityRedirectUrl: process.env.NEXT_PUBLIC_ELIGIBILITY_REDIRECT_URL ?? '/c/fitness',
    siteMapUrl,
    supportedLocales: safeParse<string[]>(process.env.NEXT_PUBLIC_SUPPORTED_LOCALES) || [
      'en-US',
      'en-CA',
      'fr-CA',
      'en-GB',
    ],
    isCartIconOnCheckout: process.env.NEXT_PUBLIC_IS_CART_ICON_ON_CHECKOUT === 'true',
    topNavDelay: safeParse<number>(process.env.NEXT_PUBLIC_TOPNAV_DELAY) ?? 200,
    middlewareTimeouts:
      safeParse<typeof MIDDLEWARE_TIMEOUTS>(process.env.NEXT_PUBLIC_MIDDLEWARE_TIMEOUTS) ||
      MIDDLEWARE_TIMEOUTS,
    inactivityTimeout: Number(safeParse<number>(process.env.NEXT_PUBLIC_INACTIVITY_TIMEOUT) || 0),
    webSourceCodesToDisableSetPassword:
      safeParse<string[]>(process.env.NEXT_PUBLIC_WEB_SOURCE_CODES_TO_DISABLE_SET_PASSWORD) || [],
    authProvider: (process.env.NEXT_PUBLIC_AUTH_PROVIDER as AuthProviders) || AuthProviders.AKAMAI,
    productMaxQuantities: safeParse(process.env.NEXT_PUBLIC_PRODUCT_MAX_QUANTITIES) || {
      digital: 1,
      nutrition: 5,
      fitness: 5,
    },
  },
  shopify: {
    env: process.env.NEXT_PUBLIC_SHOPIFY_ENV ?? process.env.NEXT_PUBLIC_ENV ?? 'local',
    baseUrlMap: safeParse(process.env.NEXT_PUBLIC_SHOPIFY_BASE_URL_MAP) || {
      US: 'https://shop.bodi.com',
      CA: 'https://shop-ca.bodi.com',
      GB: 'https://shop-uk.bodi.com',
      AU: 'https://shop.bodi.com',
      FR: 'https://shop.bodi.com',
    },
  },
  logs: {
    logsVisibleInConsole: process.env.NEXT_PUBLIC_LOGS_VISIBLE_IN_CONSOLE === 'true',
    logLevelClient: process.env.NEXT_PUBLIC_LOG_LEVEL_CLIENT ?? 'debug',
    logLevelServer: process.env.NEXT_PUBLIC_LOG_LEVEL_SERVER ?? 'info',
    sensitiveFieldPatterns: safeParse<string[]>(
      process.env.NEXT_PUBLIC_SENSITIVE_FIELD_PATTERNS
    ) || [
      'password',
      'token',
      'secret',
      'flexkey',
      'auth',
      'session',
      'cookie',
      'csrf',
      'jwt',
      'bearer',
      'firstName',
      'lastName',
      'address',
      'streetName',
      'additionalStreetInfo',
      'city',
      'zip',
      'postalCode',
      'email',
      'cardExpiration',
      'cvc',
      'cvv',
      'signature',
    ],
    safeFieldPatterns: safeParse<string[]>(process.env.NEXT_PUBLIC_SAFE_FIELD_PATTERNS) || [
      '^CustomerEmail$',
      '^customerEmailDomain$',
      '^clickIdCookie$',
      '^payerAuthenticationTransaction$',
      '^auth0ResponseHeaders$',
      '^x-auth0-requestid$',
    ],
  },
  amplitude: {
    clientDeploymentKey: process.env.NEXT_PUBLIC_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY ?? '',
    apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY ?? '',
    logLevel: Number(process.env.NEXT_PUBLIC_AMPLITUDE_LOG_LEVEL ?? 0),
  },
  builder: {
    apiKey: process.env.NEXT_PUBLIC_BUILDER_API_KEY ?? '',
    cacheMap: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_BUILDER_CACHE_MAP) ?? {
      a2c65c9f3dfa480d9c395a195ea5c0f5: CACHE.page,
      '96dd32ede8cf4d098b2b95ca0be5986f': CACHE.notFoundSection,
      '23f370c8a21b47ca834adc6ac287a1c5': CACHE.flowRouterRules,
      adc4dd6f890e4613915772a280b49ed6: CACHE.nutritionPDP,
      '6a29f9b7c39747c8ba4e0bfaf1bcc44e': CACHE.digitalPDP,
    },
  },
  cybersource: {
    iframeUrl: process.env.NEXT_PUBLIC_CYBERSOURCE_IFRAME_URL ?? '',
  },
  datadog: {
    allowedTracingUrls: safeParse(process.env.NEXT_PUBLIC_DATADOG_RUM_ALLOWED_TRACING_URLS) || [],
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APP_ID ?? '',
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN ?? '',
    env: process.env.NEXT_PUBLIC_ENV ?? 'local',
    isEnabled: process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED
      ? process.env.NEXT_PUBLIC_IS_DATADOG_ENABLED === 'true'
      : true,
    sessionSampleRate: Number(process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE ?? 100),
    sessionReplaySampleRate: Number(
      process.env.NEXT_PUBLIC_DATADOG_RUM_SAMPLE_RATE_SESSION_REPLAY ?? 100
    ),
    site: 'datadoghq.com',
    service: 'commerce-web',
  },
  featureFlags: {
    partnerEnabled: process.env.NEXT_PUBLIC_PARTNER_ENABLED === 'true',
    guestCheckoutEnabled: process.env.NEXT_PUBLIC_GUEST_CHECKOUT_ENABLED === 'true',
    myAccountPageEnabled: process.env.NEXT_PUBLIC_MY_ACCOUNT_ENABLED === 'true',
    savedAddressesEnabled: process.env.NEXT_PUBLIC_SAVED_ADDRESSES_ENABLED === 'true',
    savedPaymentsEnabled: process.env.NEXT_PUBLIC_SAVED_PAYMENTS_ENABLED === 'true',
    inspectUserPropertiesEnabled:
      process.env.NEXT_PUBLIC_INSPECT_USER_PROPERTIES_ENABLED === 'true',
    microformV2Enabled: process.env.NEXT_PUBLIC_MICROFORM_V2_ENABLED === 'true',
    checkoutLoginEnabled: process.env.NEXT_PUBLIC_CHECKOUT_LOGIN_ENABLED === 'true',
    BODEnabled: process.env.NEXT_PUBLIC_BOD_ENABLED === 'true',
    faqEnabled: process.env.NEXT_PUBLIC_FAQ_ENABLED === 'true',
    shopifyEnabled: process.env.NEXT_PUBLIC_SHOPIFY_ENABLED === 'true',
  },
  googleTagManager: {
    id: process.env.NEXT_PUBLIC_GTM_ID ?? 'GTM-NX2LQ8F',
    auth: process.env.NEXT_PUBLIC_GTM_AUTH,
    previewEnv: process.env.NEXT_PUBLIC_GTM_PREVIEW_ENV,
  },
  googleRecaptcha: {
    siteKey: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? '',
  },
  orderService: {
    apiUrl: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_KEY ?? '',
    apiGatewayUrl: process.env.NEXT_PUBLIC_ORDER_SERVICE_API_GATEWAY_URL ?? '',
  },
  eventService: {
    apiUrl: process.env.NEXT_PUBLIC_EVENT_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_EVENT_SERVICE_API_KEY ?? '',
    awsRegion: process.env.NEXT_PUBLIC_EVENT_SERVICE_AWS_REGION ?? 'us-west-2',
    awsService: process.env.NEXT_PUBLIC_EVENT_SERVICE_AWS_SERVICE ?? 'execute-api',
  },
  osano: {
    configId: process.env.NEXT_PUBLIC_OSANO_ID ?? '',
    configVersion: process.env.NEXT_PUBLIC_OSANO_VERSION ?? 'v1.3.0',
    region1CountryCodes: (
      safeParse<string[]>(process.env.NEXT_PUBLIC_OSANO_REGION1_COUNTRY_CODES) ?? ['US', 'CA']
    ).map((c) => c.toUpperCase()),
  },
  consentsService: {
    apiUrl: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_URL ?? '',
    apiKey: process.env.NEXT_PUBLIC_CONSENTS_SERVICE_API_KEY ?? '',
  },
  threatmetrix: {
    url: process.env.NEXT_PUBLIC_THREATMETRIX_URL ?? '',
    orgId: process.env.NEXT_PUBLIC_THREATMETRIX_ORG_ID ?? '',
  },
  affiliate: {
    cookieMaxAge: process.env.NEXT_PUBLIC_AFFILIATE_COOKIE_MAX_AGE ?? '10368000',
    webSourceCode: process.env.NEXT_PUBLIC_AFFILIATE_WEB_SOURCE_CODE ?? 'AFF_BODI_IMP',
  },
  loudCrowd: {
    scriptUrl: process.env.NEXT_PUBLIC_LOUDCROWD_SCRIPT_URL ?? 'https://pub.loudcrowd.com/embed.js',
    shopId: process.env.NEXT_PUBLIC_LOUDCROWD_SHOP_ID ?? 'aW50ZWdyYXRpb246ODU3',
  },
  sse: {
    scriptUrl:
      process.env.NEXT_PUBLIC_SSE_SCRIPT_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.js',
    cssUrl:
      process.env.NEXT_PUBLIC_SSE_CSS_URL ??
      'https://dev2.wine.beachbody.com/build.version-0.0.1.css',
    isSplashed: process.env.NEXT_PUBLIC_SSE_IS_SPLASHED === 'true',
    sseMigrationDate: '2025-12-01',
    isLegacySseEnabled: process.env.NEXT_PUBLIC_SSE_IS_LEGACY_ENABLED === 'true',
  },
  applePay: {
    merchantId: process.env.NEXT_PUBLIC_APPLE_MERCHANT_ID ?? 'merchant.com.bodi.non-prod',
    allowCardsFromAnyCountry:
      process.env.NEXT_PUBLIC_APPLE_PAY_ALLOW_CARDS_FROM_ANY_COUNTRY === 'true',
    isEnabled: process.env.NEXT_PUBLIC_APPLE_PAY_ENABLED === 'true',
    enableCriteria: safeParse(process.env.NEXT_PUBLIC_APPLE_PAY_ENABLE_CRITERIA) ??
      // allow apple pay for non-subscription products of any type
      [
        {
          'lineItem.productType.obj.name': "$1 === 'Digital'",
          "lineItem.variant.attributes.find(({name}) => name === 'subscriptionFrequencyCadence').value":
            "['one-time', undefined].includes($1)",
        },
        {
          'lineItem.productType.obj.name': "$1 === 'Nutrition'",
          "lineItem.variant.attributes.find(({name}) => name === 'subscriptionFrequencyCadence').value":
            "['one-time', undefined].includes($1)",
        },
        {
          'lineItem.productType.obj.name': "$1 === 'Fitness'",
          "lineItem.variant.attributes.find(({name}) => name === 'subscriptionFrequencyCadence').value":
            "['one-time', undefined].includes($1)",
        },
      ],
    env: process.env.NEXT_PUBLIC_ENV ?? 'dev',
  },
  sierra: {
    isEnabled: process.env.NEXT_PUBLIC_SIERRA_ENABLED === 'true',
    scriptUrl:
      process.env.NEXT_PUBLIC_SIERRA_SCRIPT_URL ??
      'https://sierra.chat/agent/Ut1QkpUBlHLzwwIBlHL4Ah5wk-5cYf0rK_kNTk3SH5E/embed',
    pathsToCloseChat: safeParse(process.env.NEXT_PUBLIC_SIERRA_PATHS_TO_CLOSE_CHAT) ?? [
      '/checkout',
      '/order-confirmation',
      '/print-receipt',
    ],
  },
  socialLadder: {
    isEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_ENABLED === 'true',
    isConversionEnabled: process.env.NEXT_PUBLIC_SOCIAL_LADDER_CONVERSION_IS_ENABLED === 'true',
    isSplashed: process.env.NEXT_PUBLIC_SOCIAL_LADDER_IS_SPLASHED === 'true',
    cookieMaxAge: process.env.NEXT_PUBLIC_SOCIAL_LADDER_COOKIE_MAX_AGE ?? `${ONE_MONTH_IN_SECONDS}`,
    areaGuid: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_AREA_GUID_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
    appGuid: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_APP_GUID_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
    shopCode: safeParse(process.env.NEXT_PUBLIC_SOCIAL_LADDER_SHOP_CODE_MAP) ?? {
      BodiAffiliates: '',
      BodiExperience: '',
    },
  },
  videoPlayer: {
    scriptUrl:
      process.env.NEXT_PUBLIC_VIDEO_PLAYER_SCRIPT_URL ??
      'https://bodi-web-video-player.s3.us-east-2.amazonaws.com/web-video-player.iife.js',
    configUrl:
      process.env.NEXT_PUBLIC_VIDEO_PLAYER_CONFIG_URL ??
      'https://bodi-web-video-player.s3.us-east-2.amazonaws.com/config.json',
  },
  gladly: {
    url: process.env.NEXT_PUBLIC_GLADLY_URL || 'https://bodi.us-uat.gladly.qa',
    orgId: process.env.NEXT_PUBLIC_GLADLY_ORG_ID || 'G05jz7h4QDyApoeen3QDFw',
    appId: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_GLADLY_APP_ID) ?? {
      'en-US': 'bodi.com-BODi-US-English',
      'es-US': '',
      'en-CA': 'bodi.com-BODi-Canada-English',
      'fr-CA': 'bodi.com-BODi-Canada-French',
      'en-GB': '',
      'fr-FR': '',
      'en-AU': '',
    },
    lng: {
      'en-US': 'en-us',
      'es-US': 'en-us',
      'en-CA': 'en-us',
      'fr-CA': 'fr-ca',
      'en-GB': 'en-us',
      'fr-FR': 'fr-ca',
      'en-AU': 'en-us',
    },
    audienceId: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_GLADLY_AUDIENCE_ID) ?? {
      'en-US': 'XmZehuF7QhWlvHgBLOIDgQ',
      'es-US': 'XmZehuF7QhWlvHgBLOIDgQ',
      'en-CA': 'ihhQLHZEQRKsGp28-xqWhQ',
      'fr-CA': 'ihhQLHZEQRKsGp28-xqWhQ',
      'en-GB': 'q5Nrofp4SlCsQykzvqWAyQ',
      'fr-FR': 'uRC4jbd8SaeJlgSJAsqiHQ',
      'en-AU': 'x1LiibXtTUqHdYVfEw1WRg',
    },
  },
  formsite: {
    formUrls: safeParse<Record<string, string>>(process.env.NEXT_PUBLIC_FORMSITE_FORM_URLS) ?? {
      gb: 'https://fs6.formsite.com/beachbody/ahwiyqrvvb/index',
      fr: 'https://fs6.formsite.com/beachbody/4hlkgk7ddc/index',
      au: 'https://fs6.formsite.com/beachbody/tmj6aomvgr/index',
    },
  },
}
